/** @jsxImportSource @emotion/react */
import { css, Theme } from '@emotion/react';
import '@reach/menu-button/styles.css';
import React from 'react';
import { Menu, MenuItem, MenuLink, MenuButton } from '@reach/menu-button';
import { noop } from 'lodash-es';
import { MdCheck, MdKeyboardArrowDown } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { useQueryCache } from 'react-query';

import { Flex } from 'components/styled';
import NotificationItem from 'components/tiles/NotificationItem';
import { NavLink } from 'components/styled/Link';
import Text from 'components/styled/Text';
import Button from 'components/styled/Button';

import { NotificationsIcon } from 'assets';
import { useLoggedInUser } from 'api';

import {
	markAllNotificationRead,
	useNotificationsNewest,
	useNotificationUnreadCount,
} from 'api/notificationsApi';

import { MenuAvatar, MenuHeader, StyledMenuList } from './styled';

const NotificationsBadge: React.FC = () => {
	const { t } = useTranslation('components/header/notifications_badge/');

	const response = useNotificationsNewest();
	const countResponse = useNotificationUnreadCount();
	const user = useLoggedInUser();

	const cache = useQueryCache();

	const items = response.data?.items ?? [];
	const unreadCount = countResponse.data ?? 0;

	return (
		<Menu>
			{({ isOpen }) => (
				<React.Fragment>
					<MenuAvatar
						as={MenuButton}
						aria-label={t('user_notifications')}
						disabled={items.length <= 0}
						isOpen={isOpen}
						position="relative"
					>
						{unreadCount > 0 && (
							<Flex
								justifyContent="center"
								alignItems="center"
								size={14}
								bg="error"
								color="white"
								css={css`
									position: absolute;
									top: 4px;
									right: 4px;
									border-radius: 50%;
									font-size: 11px;
								`}
							>
								{unreadCount > 99 ? '*' : unreadCount}
							</Flex>
						)}
						<NotificationsIcon />
					</MenuAvatar>

					<StyledMenuList>
						<Flex
							width={1}
							justifyContent="space-between"
							alignItems="center"
							backgroundColor="primary"
						>
							<MenuItem as={MenuHeader} onSelect={noop}>
								{t('notifications')}
							</MenuItem>
							{unreadCount > 0 && (
								<MenuItem
									as={Button}
									onSelect={async () => {
										await markAllNotificationRead(user?.personType);
										cache.invalidateQueries('notifications');
									}}
									fontSize="sm"
									fontWeight="bold"
									variant="primary"
								>
									<MdCheck size={18} />
									<Text ml={2}>{t('all_read')}</Text>
								</MenuItem>
							)}
						</Flex>

						{items.map(item => (
							<MenuItem key={item.id} onSelect={noop}>
								<NotificationItem {...item} variant="header" />
							</MenuItem>
						))}

						<MenuLink
							as={NavLink}
							to="/notifications"
							css={(theme: Theme) => css`
								&[data-reach-menu-item] {
									background: ${theme.colors.lightGrey};
									padding: 0;
									text-decoration: none;
								}
							`}
						>
							<Flex
								justifyContent="center"
								alignItems="center"
								color="primary"
								py={2}
							>
								<Text mr={1} fontWeight="bold">
									{t('show_all')}
								</Text>
								<MdKeyboardArrowDown size={20} />
							</Flex>
						</MenuLink>
					</StyledMenuList>
				</React.Fragment>
			)}
		</Menu>
	);
};

export default NotificationsBadge;
